import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, KeyRound, Trash2, Copy, Edit3, UserX, UserCheck } from "lucide-react";
import PasswordStrength, { evaluatePassword } from "@/components/PasswordStrength";
import { VALID_ROLES } from "@/lib/roles";

/** Generates a 14-char password guaranteed to satisfy policy (upper+lower+digit+symbol). */
function randomPassword(len = 14) {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghjkmnpqrstuvwxyz";
  const digit = "23456789";
  const sym = "!#$%&*+?@";
  const all = upper + lower + digit + sym;
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  const req = [pick(upper), pick(lower), pick(digit), pick(sym)];
  const rest = Array.from({ length: Math.max(len, 12) - req.length }, () => pick(all));
  const chars = [...req, ...rest];
  // shuffle
  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role: "customer", temp_password: randomPassword(), must_change_password: true });
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState(randomPassword());
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "customer", must_change_password: false });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const { rulesPassed } = evaluatePassword(form.temp_password);
    if (!rulesPassed) { toast.error("Temporary password does not meet strength requirements"); return; }
    setBusy(true);
    try {
      await api.post("/admin/users", form);
      toast.success("User created. Share the temp password securely.");
      copyToClipboard(`${form.email} / ${form.temp_password}`);
      setForm({ email: "", name: "", role: "customer", temp_password: randomPassword(), must_change_password: true });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Create failed"); }
    setBusy(false);
  };

  const reset = async () => {
    if (!resetTarget) return;
    const { rulesPassed } = evaluatePassword(resetPw);
    if (!rulesPassed) { toast.error("Password does not meet strength requirements"); return; }
    try {
      await api.post(`/admin/users/${resetTarget.id}/reset-password`, { new_password: resetPw, must_change_password: true });
      toast.success("Password reset. Copied to clipboard.");
      copyToClipboard(`${resetTarget.email} / ${resetPw}`);
      setResetTarget(null); setResetPw(randomPassword());
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Reset failed"); }
  };

  const del = async (u) => {
    const msg = `PERMANENT DELETE — this cannot be undone.\n\nFor departing users, "Deactivate" is usually the right choice — it keeps their history intact.\n\nType the email to confirm: ${u.email}`;
    const confirmation = prompt(msg);
    if (confirmation !== u.email) {
      if (confirmation !== null) toast.error("Email did not match — delete cancelled");
      return;
    }
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User permanently deleted"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const deactivate = async (u) => {
    const reason = prompt(`Deactivate ${u.email}?\n\nOptional reason (visible to admins only):`, "");
    if (reason === null) return; // cancelled
    try {
      await api.post(`/admin/users/${u.id}/deactivate`, { reason });
      toast.success(`${u.email} deactivated. They can no longer sign in.`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Deactivate failed"); }
  };

  const reactivate = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/reactivate`);
      toast.success(`${u.email} reactivated.`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Reactivate failed"); }
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setEditForm({ name: u.name || "", role: u.role, must_change_password: !!u.must_change_password });
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    try {
      await api.patch(`/admin/users/${editTarget.id}`, editForm);
      toast.success(`Updated ${editTarget.email}`);
      setEditTarget(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  const copyToClipboard = (text) => {
    if (navigator?.clipboard) navigator.clipboard.writeText(text).catch(() => {});
  };

  return (
    <div data-testid="admin-users-page">
      <div className="eyebrow mb-2">Access</div>
      <h1 className="aperture-h1 text-4xl mb-10">Users.</h1>

      <form onSubmit={create} className="bg-white p-6 rounded-sm border mb-10 grid md:grid-cols-6 gap-3 items-end" style={{ borderColor: "rgba(10,37,64,0.10)" }} data-testid="admin-user-create-form">
        <div className="md:col-span-6 eyebrow">Create User</div>
        <div className="md:col-span-2">
          <label className="text-xs text-navy/60 block mb-1">Email</label>
          <input required type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} data-testid="user-form-email"
            className="w-full px-3 py-2.5 border rounded-sm text-sm focus:outline-none focus:border-gold" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
        </div>
        <div className="md:col-span-1">
          <label className="text-xs text-navy/60 block mb-1">Name</label>
          <input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})}
            className="w-full px-3 py-2.5 border rounded-sm text-sm focus:outline-none focus:border-gold" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
        </div>
        <div className="md:col-span-1">
          <label className="text-xs text-navy/60 block mb-1">Role</label>
          <select value={form.role} onChange={(e)=>setForm({...form, role:e.target.value})} data-testid="user-form-role"
            className="w-full px-3 py-2.5 border rounded-sm text-sm focus:outline-none focus:border-gold bg-white" style={{ borderColor: "rgba(10,37,64,0.15)" }}>
            <option value="customer">customer</option>
            <option value="admin">admin</option>
              <option value="customer">customer</option>
              <option value="admin">admin</option>
              <option value="viewer">viewer</option>
              <option value="agent">agent</option>
              <option value="ops_staff">ops_staff</option>
              <option value="ops_admin">ops_admin</option>
              <option value="superadmin">superadmin</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs text-navy/60 block mb-1">Temporary password</label>
          <div className="flex gap-2">
            <input required value={form.temp_password} onChange={(e)=>setForm({...form, temp_password:e.target.value})} data-testid="user-form-temp-password"
              className="flex-1 px-3 py-2.5 border rounded-sm text-sm font-mono focus:outline-none focus:border-gold" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
            <button type="button" onClick={()=>setForm({...form, temp_password: randomPassword()})} data-testid="user-form-generate"
              className="px-3 py-2.5 border border-navy text-navy text-xs hover:bg-navy hover:text-white transition-colors rounded-sm">Regen</button>
          </div>
          <PasswordStrength password={form.temp_password} />
        </div>
        <label className="md:col-span-4 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.must_change_password} onChange={(e)=>setForm({...form, must_change_password:e.target.checked})} data-testid="user-form-must-change" />
          Force user to change password on first login
        </label>
        <div className="md:col-span-2">
          <button disabled={busy} data-testid="user-form-submit" className="btn-primary w-full py-2.5 text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2 rounded-sm">
            <Plus className="w-3.5 h-3.5"/>{busy ? "Creating…" : "Create User"}
          </button>
        </div>
        <div className="md:col-span-6 text-xs text-navy/50">The email + temp password will be copied to your clipboard on create — share via a secure channel.</div>
      </form>

      <div className="bg-white rounded-sm border overflow-x-auto" style={{ borderColor: "rgba(10,37,64,0.10)" }}>
        <table className="w-full text-sm">
          <thead className="bg-navy/5"><tr>{["Email","Name","Role","Status","Force Change","Created","Actions"].map(h=><th key={h} className="px-4 py-3 text-left eyebrow" style={{color:"#0A2540"}}>{h}</th>)}</tr></thead>
          <tbody>
            {users.length === 0 && <tr><td colSpan="7" className="px-4 py-10 text-center text-navy/50">No users.</td></tr>}
            {users.map((u) => (
              <tr key={u.id} className={`border-t ${u.active === false ? "opacity-60 bg-navy/[0.02]" : ""}`} data-testid={`user-row-${u.email}`} style={{ borderColor: "rgba(10,37,64,0.08)" }}>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3 text-navy/70">{u.name || "—"}</td>
                <td className="px-4 py-3"><span className={`text-xs uppercase tracking-widest ${u.role === "admin" || u.role === "superadmin" ? "text-gold" : "text-navy/70"}`}>{u.role}</span></td>
                <td className="px-4 py-3">
                  {u.active === false
                    ? <span data-testid="status-inactive" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest" style={{ backgroundColor: "rgba(181,77,77,0.15)", color: "#B54D4D" }}>● Inactive</span>
                    : <span data-testid="status-active" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest" style={{ backgroundColor: "rgba(78,122,92,0.15)", color: "#4E7A5C" }}>● Active</span>
                  }
                  {u.deactivated_reason && <div className="text-[10px] text-navy/50 mt-1 max-w-[180px] truncate" title={u.deactivated_reason}>“{u.deactivated_reason}”</div>}
                </td>
                <td className="px-4 py-3 text-xs">{u.must_change_password ? <span className="text-gold">yes</span> : <span className="text-navy/50">no</span>}</td>
                <td className="px-4 py-3 text-navy/60 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <button onClick={()=>openEdit(u)} data-testid={`user-edit-${u.email}`}
                    className="inline-flex items-center gap-1 text-xs text-navy hover:text-gold mr-4"><Edit3 className="w-3 h-3"/>Edit</button>
                  <button onClick={()=>{setResetTarget(u); setResetPw(randomPassword());}} data-testid={`user-reset-${u.email}`}
                    className="inline-flex items-center gap-1 text-xs text-navy hover:text-gold mr-4"><KeyRound className="w-3 h-3"/>Reset password</button>
                  {u.active === false
                    ? <button onClick={()=>reactivate(u)} data-testid={`user-reactivate-${u.email}`}
                        className="inline-flex items-center gap-1 text-xs text-gold hover:text-navy mr-4"><UserCheck className="w-3 h-3"/>Reactivate</button>
                    : <button onClick={()=>deactivate(u)} data-testid={`user-deactivate-${u.email}`}
                        className="inline-flex items-center gap-1 text-xs text-navy hover:text-gold mr-4"><UserX className="w-3 h-3"/>Deactivate</button>
                  }
                  <button onClick={()=>del(u)} data-testid={`user-delete-${u.email}`}
                    className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800"><Trash2 className="w-3 h-3"/>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {resetTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50" data-testid="reset-password-modal"><div className="bg-white rounded-sm shadow-2xl max-w-md w-full p-8">
            <div className="eyebrow mb-2">Reset Password</div>
            <div className="font-serif text-2xl text-navy mb-1">{resetTarget.email}</div>
            <p className="text-sm text-navy/60 mb-6">Set a temporary password. The user will be forced to change it on next login.</p>
            <div className="flex gap-2 mb-4">
              <input value={resetPw} onChange={(e)=>setResetPw(e.target.value)} data-testid="reset-pw-input"
                className="flex-1 px-3 py-2.5 border rounded-sm text-sm font-mono focus:outline-none focus:border-gold" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
              <button onClick={()=>setResetPw(randomPassword())} className="px-3 py-2.5 border border-navy text-navy text-xs hover:bg-navy hover:text-white transition-colors rounded-sm">Regen</button>
              <button onClick={()=>copyToClipboard(resetPw)} className="px-3 py-2.5 border border-navy text-navy text-xs hover:bg-navy hover:text-white transition-colors rounded-sm inline-flex items-center gap-1"><Copy className="w-3 h-3"/></button>
            </div>
            <PasswordStrength password={resetPw} />
            <div className="flex justify-end gap-2">
              <button onClick={()=>setResetTarget(null)} className="px-4 py-2 text-xs uppercase tracking-widest text-navy/70 hover:text-navy">Cancel</button>
              <button onClick={reset} data-testid="reset-pw-confirm" className="btn-primary px-5 py-2 text-xs uppercase tracking-widest rounded-sm">Reset & Copy</button>
            </div>
          </div>
        </div>
      )}

      {editTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50" data-testid="edit-user-modal">
          <div className="bg-white rounded-sm shadow-2xl max-w-md w-full p-8">
            <div className="eyebrow mb-2">Edit User</div>
            <div className="font-serif text-2xl text-navy mb-1">{editTarget.email}</div>
            <p className="text-xs text-navy/50 mb-6">Change name, role, or the force-change-password flag. Purchase history, affiliate activity, and library remain linked.</p>

            <div className="space-y-4">
              <div>
                <label className="text-xs text-navy/60 block mb-1">Name</label>
                <input value={editForm.name} onChange={(e)=>setEditForm({...editForm, name:e.target.value})} data-testid="edit-user-name"
                  className="w-full px-3 py-2.5 border rounded-sm text-sm focus:outline-none focus:border-gold" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
              </div>
              <div>
                <label className="text-xs text-navy/60 block mb-1">Role</label>
                <select value={editForm.role} onChange={(e)=>setEditForm({...editForm, role:e.target.value})} data-testid="edit-user-role"
                  className="w-full px-3 py-2.5 border rounded-sm text-sm focus:outline-none focus:border-gold bg-white" style={{ borderColor: "rgba(10,37,64,0.15)" }}>
                  {VALID_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <p className="mt-1 text-[11px] text-navy/50">Changing role takes effect on this user's next request. Their JWT will keep the old role until they sign out and back in.</p>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editForm.must_change_password} onChange={(e)=>setEditForm({...editForm, must_change_password:e.target.checked})} data-testid="edit-user-must-change" />
                Force password change on next login
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button onClick={()=>setEditTarget(null)} className="px-4 py-2 text-xs uppercase tracking-widest text-navy/70 hover:text-navy">Cancel</button>
              <button onClick={saveEdit} data-testid="edit-user-save" className="btn-primary px-5 py-2 text-xs uppercase tracking-widest rounded-sm">Save Changes</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
